import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from '../slices/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithLogout = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    window.location.href = '/login';
  }

  return result;
};

// Super-admin payout review (spec §6). All endpoints under /admin/payouts.
// `getReview` drives the six panels + decision bar; the mutations re-run the
// server-authoritative check engine, so the screen refetches the dossier after each.
export const payoutReviewApi = createApi({
  reducerPath: 'payoutReviewApi',
  baseQuery: baseQueryWithLogout,
  tagTypes: ['Review'],
  endpoints: (builder) => ({
    // Full review dossier: { request, balance, merchant, destination, activity, flags, canApprove, lockedBy }
    getReview: builder.query({
      query: (requestId) => `/admin/payouts/${requestId}/review`,
      transformResponse: (response) => response?.data || null,
      providesTags: (result, error, requestId) => [{ type: 'Review', id: requestId }],
    }),

    // Soft-claim the request (RACE-1). 409 if locked by another admin.
    claim: builder.mutation({
      query: (requestId) => ({
        url: `/admin/payouts/${requestId}/claim`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, requestId) => [{ type: 'Review', id: requestId }],
    }),

    // Release the soft-claim (on unmount / navigate away).
    releaseClaim: builder.mutation({
      query: (requestId) => ({
        url: `/admin/payouts/${requestId}/claim`,
        method: 'DELETE',
      }),
    }),

    // Acknowledge a WARN flag (logged). Body: { ack_reason }.
    ackFlag: builder.mutation({
      query: ({ requestId, code, ackReason }) => ({
        url: `/admin/payouts/${requestId}/flags/${code}/ack`,
        method: 'POST',
        body: { ack_reason: ackReason },
      }),
      invalidatesTags: (result, error, { requestId }) => [{ type: 'Review', id: requestId }],
    }),

    // Approve & release. Re-runs engine. Body: { txHash, overrideReason? }.
    approve: builder.mutation({
      query: ({ requestId, txHash, overrideReason }) => ({
        url: `/payouts/admin/${requestId}/approve`,
        method: 'POST',
        body: { txHash, ...(overrideReason ? { overrideReason } : {}) },
      }),
      invalidatesTags: (result, error, { requestId }) => [{ type: 'Review', id: requestId }],
    }),
  }),
});

export const {
  useGetReviewQuery,
  useClaimMutation,
  useReleaseClaimMutation,
  useAckFlagMutation,
  useApproveMutation,
} = payoutReviewApi;
